'use client'

import { DEFAULT_BUSINESS_PROFILE_LOGO } from '@/lib/constants/business-profile'
import {
  useBusinessProfile,
  useBusinessProfileSwitching,
} from '@/lib/contexts/BusinessProfileContext'
import {
  useGetBusinessProfilesQuery,
  useSetDefaultBusinessProfileMutation,
} from '@/lib/features/businessProfile'
import { BusinessProfile } from '@/lib/features/businessProfile/types'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, ChevronDown, Edit, Plus, Trash2 } from 'lucide-react'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

interface BusinessProfileDropdownProps {
  onCreateProfile?: () => void
  onEditProfile?: (profile: BusinessProfile) => void
  onDeleteProfile?: (profile: BusinessProfile) => void
}

export function BusinessProfileDropdown({
  onCreateProfile,
  onEditProfile,
  onDeleteProfile,
}: BusinessProfileDropdownProps) {
  const { data: profilesResponse, isLoading, error } = useGetBusinessProfilesQuery()

  const { currentProfile, setCurrentProfile } = useBusinessProfile()
  const { setIsProfileSwitching } = useBusinessProfileSwitching()
  const router = useRouter()
  const pathname = usePathname()
  const [setDefaultProfile] = useSetDefaultBusinessProfileMutation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const profiles = profilesResponse?.data || []

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSetDefault = async (profileId: string) => {
    try {
      // Show loading animation immediately
      setIsProfileSwitching(true)
      setIsOpen(false)

      // Update the profile on the server
      await setDefaultProfile(profileId).unwrap()

      // Update the current profile in context (optimistic update)
      const newCurrentProfile = profiles.find((p) => p.id === profileId)
      if (newCurrentProfile) {
        setCurrentProfile(newCurrentProfile)
      }

      toast.success(`Switched to ${newCurrentProfile?.name || 'business profile'}`)

      // Check if user is currently on a thread page
      const isOnThreadPage = pathname.startsWith('/copilot/') && pathname !== '/copilot'

      // Brief delay to show loading animation, then navigate appropriately
      setTimeout(() => {
        if (isOnThreadPage) {
          // If on a thread page, redirect to /copilot to start fresh with new profile
          router.push('/copilot')
        } else {
          // For other pages, refresh to reload with new profile data
          router.refresh()
        }
        // Loading will be hidden after navigation completes
        setTimeout(() => setIsProfileSwitching(false), 1000)
      }, 800)
    } catch (error) {
      setIsProfileSwitching(false)
      toast.error('Failed to update default profile')
      console.error('Failed to set default profile:', error)
    }
  }

  const handleCreateClick = () => {
    onCreateProfile?.()
    setIsOpen(false)
  }

  const handleEditClick = (profile: BusinessProfile) => {
    onEditProfile?.(profile)
    setIsOpen(false)
  }

  const handleDeleteClick = (profile: BusinessProfile) => {
    onDeleteProfile?.(profile)
    setIsOpen(false)
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-4 w-20 animate-pulse rounded bg-gray-200"></div>
        <ChevronDown className="h-4 w-4 text-gray-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-600">
        <span className="text-sm">Error loading profiles</span>
        <ChevronDown className="h-4 w-4" />
      </div>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 rounded-md hover:bg-gray-100 focus:outline-none"
      >
        <div className="relative h-10 w-10">
          <Image
            alt="Sprites logo"
            src="/sprites.svg"
            width={40}
            height={40}
            className="h-10 w-10 cursor-pointer hover:opacity-80"
          />
        </div>
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 z-50 mt-1 w-64 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          >
            {/* Profile List */}
            <div className="max-h-48 overflow-y-auto">
              {profiles.map((profile) => (
                <div
                  key={profile.id}
                  className="group flex items-center justify-between px-4 py-2 hover:bg-gray-50"
                >
                  <button
                    onClick={() =>
                      currentProfile?.id !== profile.id && handleSetDefault(profile.id)
                    }
                    disabled={currentProfile?.id === profile.id}
                    className="flex flex-1 items-center gap-3 text-left"
                  >
                    <div className="relative h-8 w-8">
                      <Image
                        alt={profile.name}
                        src={profile.logoImageUrl || DEFAULT_BUSINESS_PROFILE_LOGO}
                        width={32}
                        height={32}
                        className="h-8 w-8 rounded-full object-cover"
                        onError={(e) => {
                          // Fallback to default logo on error
                          const target = e.target as HTMLImageElement
                          target.src = DEFAULT_BUSINESS_PROFILE_LOGO
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{profile.name}</span>
                        {currentProfile?.id === profile.id && (
                          <Check className="h-4 w-4 text-green-500" />
                        )}
                      </div>
                    </div>
                  </button>

                  <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      onClick={() => handleEditClick(profile)}
                      className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                      title="Edit profile"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                    {currentProfile?.id !== profile.id && (
                      <button
                        onClick={() => handleDeleteClick(profile)}
                        className="rounded p-1 text-gray-400 hover:bg-red-100 hover:text-red-600"
                        title="Delete profile"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Create New Profile */}
            <div>
              <button
                onClick={handleCreateClick}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" />
                Create New Profile
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
